'use client';

import { cn } from '@/lib/utils';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import React, { useMemo } from 'react';
import { slugify } from '@/lib/slugify';
import { useSuspenseQueries, UseSuspenseQueryResult } from '@tanstack/react-query';
import { Product, ProductResponse } from '@/types/product';
import { getProducts } from '@/server/actions/getProductsAction';
/*import productsJson from '@/data/products.json';*/

/*const products = [
  {
    category: 'Products',
    items: [
      { category_name: 'Pipe Hangers', href: '/products/pipe-hangers' },
      { category_name: 'Roller Supports', href: '/products/roller-supports' },
      { category_name: 'Rubber Supports Inserts', href: '/products/rubber-supports-inserts' },
      { category_name: 'Anti Vibration Solutions', href: '/products/anti-vibration-solutions' },
      { category_name: 'Solar Accessories', href: '/products/solar-accessories' },
      { category_name: 'Channel Support', href: '/products/channel-support' },
      { category_name: 'Fastener Solutions', href: '/products/fastener-solutions' },
      { category_name: 'Hanging Attachments', href: '/products/hanging-attachments' },
      { category_name: 'Solar Mounting Brackets', href: '/products/solar-mounting-brackets' },
      { category_name: 'Dielectric Union', href: '/products/dielectric-union' },
      { category_name: 'Floor Drain', href: '/products/floor-drain' },
      { category_name: 'Single Pipe, Traverse Brace', href: '/products/single-pipe-traverse-brace' },
      { category_name: 'Stainless Steel Flanges', href: '/products/stainless-steel-flanges' },
      { category_name: 'All Products', href: '/products' },
    ],
  },
];*/

const more = [
  {
    category: 'More',
    items: [
      {
        title: 'About Us',
        href: '/about',
      },
      {
        title: 'Careers',
        href: '/careers',
      },
      {
        title: 'Contact',
        href: '/contact',
      },
    ],
  },
];

export default function NavbarClient() {
  const queries: [UseSuspenseQueryResult<ProductResponse>, Error] = useSuspenseQueries({
    queries: [
      {
        queryKey: ['get_products'],
        queryFn: getProducts,
      },
    ],
  });

  const productsQuery: UseSuspenseQueryResult<ProductResponse> = queries[0];

  const productsData: Product[] = useMemo(() => {
    return Array.isArray(productsQuery?.data.data) ? productsQuery.data.data : [];
  }, [productsQuery?.data.data]);

  /*  const productsData = productsJson.data*/

  const categoryItems = Array.from(
    new Set(productsData.map((p) => p.category_name).filter(Boolean)),
  ).map((category_name) => ({
    category_name,
    href: `/products/${slugify(category_name)}`,
  }));

  categoryItems.push({ category_name: 'All Products', href: '/products' });

  const products = [
    {
      category: 'Products',
      items: categoryItems,
    },
  ];

  const pathname: string = usePathname();
  const isActive = (href: string) => pathname.startsWith(href);

  return (
    <div className={'flex gap-4 font-sans tracking-tight uppercase'}>
      <NavigationMenu viewport={true} className={'hidden lg:flex'}>
        <NavigationMenuList className={'gap-3'}>
          <NavigationMenuItem>
            <NavigationMenuTrigger
              className={cn(
                navigationMenuTriggerStyle(),
                `text-muted-foreground bg-transparent font-sans text-sm uppercase hover:bg-transparent
                focus:bg-transparent dark:bg-transparent dark:hover:bg-transparent`,
                isActive('/products') && 'text-accent-foreground bg-transparent',
              )}>
              <Link prefetch={true} href="/products">
                Products
              </Link>
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="m-2 flex w-max">
                {products.map((category, index) => (
                  <div
                    key={category.category}
                    className={cn(
                      'flex-1',
                      index > 0 && 'border-l pl-4',
                      index < 2 && 'pr-0',
                    )}>
                    <div className="grid grid-cols-3">
                      {category.items.map((item, index) => {
                        const isAll =
                          item.category_name === 'All Products' ||
                          index === category.items.length - 1;

                        return (
                          <Link
                            key={item.category_name}
                            href={item.href}
                            className={cn(
                              `text-muted-foreground hover:text-foreground flex items-center gap-3 rounded-md px-6 py-2
                              transition-all duration-100 ease-in-out`,
                            )}
                            data-slot="item">
                            <div>
                              <div
                                className={cn(
                                  'w-max text-sm font-medium',
                                  isAll ? 'text-primary/80 hover:text-primary font-bold' : '',
                                )}>
                                {item.category_name}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>

          <NavigationMenuItem className={'md:hidden lg:block'}>
            <Link
              prefetch={true}
              href="/segments"
              className={cn(
                navigationMenuTriggerStyle(),
                'text-muted-foreground bg-transparent text-sm hover:bg-transparent focus:bg-transparent',
                isActive('/segments') && 'text-accent-foreground bg-transparent',
              )}>
              Segments
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem className={'md:hidden lg:block'}>
            <Link
              prefetch={true}
              href="/sectors"
              className={cn(
                navigationMenuTriggerStyle(),
                'text-muted-foreground bg-transparent text-sm hover:bg-transparent focus:bg-transparent',
                isActive('/sectors') && 'text-accent-foreground bg-transparent',
              )}>
              Sectors
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem className={'md:hidden lg:block'}>
            <Link
              prefetch={true}
              href="/services"
              className={cn(
                navigationMenuTriggerStyle(),
                'text-muted-foreground bg-transparent text-sm hover:bg-transparent focus:bg-transparent',
                isActive('/services') && 'text-accent-foreground bg-transparent',
              )}>
              Services
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem>
            <Link
              prefetch={true}
              href="/about"
              className={cn(
                navigationMenuTriggerStyle(),
                'text-muted-foreground bg-transparent text-sm hover:bg-transparent focus:bg-transparent',
                isActive('/about') && 'text-accent-foreground bg-transparent',
              )}>
              About Us
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem className={'md:hidden lg:block'}>
            <Link
              prefetch={true}
              href="/careers"
              className={cn(
                navigationMenuTriggerStyle(),
                'text-muted-foreground bg-transparent text-sm hover:bg-transparent focus:bg-transparent',
                isActive('/careers') && 'text-accent-foreground bg-transparent',
              )}>
              Careers
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem>
            <Link
              prefetch={true}
              href="/contact"
              className={cn(
                navigationMenuTriggerStyle(),
                'text-muted-foreground bg-transparent text-sm hover:bg-transparent focus:bg-transparent',
                isActive('/contact') && 'text-accent-foreground bg-transparent',
              )}>
              Contact
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem className={'md:block lg:hidden'}>
            <NavigationMenuTrigger
              className={cn(
                navigationMenuTriggerStyle(),
                `text-muted-foreground bg-transparent text-sm hover:bg-transparent focus:bg-transparent
                dark:bg-transparent dark:hover:bg-transparent`,
                isActive('/products') && 'text-accent-foreground bg-transparent',
              )}>
              More
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="m-2 flex w-max">
                {more.map((m, index) => (
                  <div
                    key={m.category}
                    className={cn(
                      'flex-1',
                      index > 0 && 'border-l pl-4',
                      index < 2 && 'pr-0',
                    )}>
                    <div className="grid grid-cols-1">
                      {m.items.map((item) => {
                        return (
                          <Link
                            key={item.title}
                            href={item.href}
                            className={cn(
                              `text-muted-foreground hover:text-foreground flex items-center gap-3 rounded-md px-6 py-2
                              transition-all duration-100 ease-in-out`,
                            )}
                            data-slot="item">
                            <div>
                              <div className={cn('w-max text-sm font-medium')}>
                                {item.title}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>
    </div>
  );
}

const ListItem = React.forwardRef<React.ElementRef<'a'>, React.ComponentPropsWithoutRef<'a'>>(
  ({ className, title, children, ...props }, ref) => {
    return (
      <li>
        <NavigationMenuLink asChild>
          <a
            ref={ref}
            className={cn(
              `hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex
              flex-row gap-3 space-y-1 rounded-md p-3 leading-none no-underline transition-colors
              outline-none select-none`,
              className,
            )}
            {...props}>
            <Image
              width={16}
              height={16}
              className={'h-10 w-10'}
              src="/globe.svg"
              alt="Product Image"
            />
            <div>
              <div className="text-sm leading-none font-medium">{title}</div>
              <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                {children}
              </p>
            </div>
          </a>
        </NavigationMenuLink>
      </li>
    );
  },
);
ListItem.displayName = 'ListItem';
